/**
 * The list of files a visitor selected on a converter page: validation of
 * new files, order (the combine order is sent as the job's upload list) and
 * the state of each file. Pure model, no DOM.
 */

import { extensionOf } from "../lib/format";

export type ItemState =
  | { readonly kind: "ready" }
  | { readonly kind: "uploading"; readonly fraction: number }
  | { readonly kind: "uploaded" }
  | { readonly kind: "converting"; readonly percent: number }
  | { readonly kind: "done" }
  | { readonly kind: "failed"; readonly message: string };

export interface QueueItem {
  readonly key: string;
  readonly file: File;
  /** Id of the stored upload; null until uploaded and again once a job used it. */
  uploadId: string | null;
  state: ItemState;
}

export interface AddRules {
  /** Accepted extensions (lower case, without the dot). */
  readonly extensions: ReadonlySet<string>;
  /** How many unfinished files the list may hold. */
  readonly maxFiles: number;
  readonly maxBytes: number;
}

export interface AddResult {
  readonly added: readonly QueueItem[];
  readonly unsupported: readonly string[];
  readonly tooLarge: readonly string[];
  readonly overLimit: readonly string[];
}

export class UploadQueue {
  private list: QueueItem[] = [];
  private nextKey = 1;

  get items(): readonly QueueItem[] {
    return this.list;
  }

  /** Files that still count against the limit (not converted yet). */
  get unfinished(): readonly QueueItem[] {
    return this.list.filter((item) => item.state.kind !== "done");
  }

  /** Files the next "Start!" works on (new, failed, or uploaded and waiting for the combine job). */
  get runnable(): readonly QueueItem[] {
    return this.list.filter(
      (item) =>
        item.state.kind === "ready" ||
        item.state.kind === "failed" ||
        item.state.kind === "uploaded",
    );
  }

  add(files: Iterable<File>, rules: AddRules): AddResult {
    const added: QueueItem[] = [];
    const unsupported: string[] = [];
    const tooLarge: string[] = [];
    const overLimit: string[] = [];
    let room = rules.maxFiles - this.unfinished.length;
    for (const file of files) {
      if (!rules.extensions.has(extensionOf(file.name))) {
        unsupported.push(file.name);
      } else if (file.size > rules.maxBytes) {
        tooLarge.push(file.name);
      } else if (room <= 0) {
        overLimit.push(file.name);
      } else {
        const item: QueueItem = {
          key: `file-${this.nextKey}`,
          file,
          uploadId: null,
          state: { kind: "ready" },
        };
        this.nextKey += 1;
        room -= 1;
        this.list.push(item);
        added.push(item);
      }
    }
    return { added, unsupported, tooLarge, overLimit };
  }

  find(key: string): QueueItem | undefined {
    return this.list.find((item) => item.key === key);
  }

  indexOf(key: string): number {
    return this.list.findIndex((item) => item.key === key);
  }

  remove(key: string): QueueItem | undefined {
    const item = this.find(key);
    this.list = this.list.filter((other) => other.key !== key);
    return item;
  }

  /** Move an item to `index` (clamped); false when nothing moved. */
  moveTo(key: string, index: number): boolean {
    const from = this.indexOf(key);
    const item = this.list[from];
    if (item === undefined) return false;
    const to = Math.min(Math.max(index, 0), this.list.length - 1);
    if (to === from) return false;
    this.list = this.list.toSpliced(from, 1).toSpliced(to, 0, item);
    return true;
  }

  /** Move an item up (negative) or down (positive) by `delta` places. */
  move(key: string, delta: number): boolean {
    const from = this.indexOf(key);
    return from >= 0 && this.moveTo(key, from + delta);
  }

  clear(): QueueItem[] {
    const removed = this.list;
    this.list = [];
    return removed;
  }
}

/**
 * Where a dragged row lands: the index of the row it was dropped on, before
 * or after it, in the list without the dragged row.
 */
export function dropIndex(from: number, over: number, after: boolean): number {
  const target = after ? over + 1 : over;
  return from < target ? target - 1 : target;
}
